// src/Login.js
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';

const Login = ({ setAuthToken }) => {
    const [token, setToken] = useState('');
    const [username, setUsername] = useState('');
    const [password, setPassword] = useState('');
    const [errorMessage, setErrorMessage] = useState('');
    const [shopOpen, setShopOpen] = useState(false);
    const [generalMessage, setGeneralMessage] = useState('');
    const [generalMessageInput, setGeneralMessageInput] = useState('');
    const [saveFeedback, setSaveFeedback] = useState('');
    const [isRestoringSession, setIsRestoringSession] = useState(false);
    const [isLoggingIn, setIsLoggingIn] = useState(false);
    const [isTogglingShop, setIsTogglingShop] = useState(false);
    const [isSavingMessage, setIsSavingMessage] = useState(false);

    const fetchAdminParams = async (authHeader) => {
        const shopOpenUrl = process.env.REACT_APP_BASE_API_URL + 'ApplicationParams/shopOpen';
        const generalMessageUrl = process.env.REACT_APP_BASE_API_URL + 'ApplicationParams/generalMessage';

        const [shopOpenResponse, generalMessageResponse] = await Promise.all([
            fetch(shopOpenUrl, { method: 'GET', headers: { 'Authorization': authHeader } }),
            fetch(generalMessageUrl, { method: 'GET', headers: { 'Authorization': authHeader } }),
        ]);

        if (shopOpenResponse.ok) {
            const shopOpenData = await shopOpenResponse.json();
            setShopOpen(shopOpenData.value === 'True');
        }

        if (generalMessageResponse.ok) {
            const generalMessageData = await generalMessageResponse.json();
            const value = generalMessageData.value || '';
            setGeneralMessage(value);
            setGeneralMessageInput(value);
        }
    };

    // check if there is a token in the cookie, if so, set it as the token
    useEffect(() => {
        var cookieToken = getCookie('token');
        if (cookieToken) {
            console.log('cookieToken: ' + cookieToken);
            setAuthToken(cookieToken);
            setToken(cookieToken);
            setIsRestoringSession(true);
            fetchAdminParams(cookieToken).catch(() => {
                setErrorMessage('Kon admininstellingen niet ophalen.');
            }).finally(() => {
                setIsRestoringSession(false);
            });
        }
    }, [setAuthToken]);

    const handleLogin = (e) => {
        e.preventDefault();
        setErrorMessage('');
        setSaveFeedback('');
        setIsLoggingIn(true);
        const token = 'Basic ' + btoa(username + ':' + password);
        //setAuthToken(token);
        //navigate('/products');
        fetchAdminParams(token)
            .then(() => {
                setAuthToken(token);
                setToken(token);

                // Calculate expiration date for 16 hours from now
                const date = new Date();
                date.setTime(date.getTime() + (16 * 60 * 60 * 1000)); // 16 hours in milliseconds
                const expires = 'expires=' + date.toUTCString();
                // Set cookie with expiration
                document.cookie = 'token=' + token + ';' + expires + ';path=/';
            })
            .catch(() => {
                setErrorMessage('Username or password incorrect');
            })
            .finally(() => {
                setIsLoggingIn(false);
            });
    };

    function toggleShopTo(val, stateStr) {
        const userConfirmed = window.confirm(`Are you sure you want to ${stateStr} the shop?`);
        if (userConfirmed) {
            const url = process.env.REACT_APP_BASE_API_URL + 'ApplicationParams';
            setSaveFeedback('');
            setIsTogglingShop(true);

            // Assuming there's an endpoint /api/auth to validate the token
            fetch(url, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': token,
                },
                body: JSON.stringify({ key: 'shopOpen', value: val }),
            })
                .then(response => {
                    if (!response.ok) {
                        throw new Error('Unable to update shop status.');
                    }
                    setShopOpen(val === 'True');
                    setSaveFeedback(`Shop status opgeslagen: ${val === 'True' ? 'OPEN' : 'CLOSED'}.`);
                })
                .catch(error => {
                    console.error('There was an error!', error);
                    setSaveFeedback('Shop status opslaan mislukt.');
                })
                .finally(() => {
                    setIsTogglingShop(false);
                });
        }
    }

    function saveGeneralMessage() {
        const url = process.env.REACT_APP_BASE_API_URL + 'ApplicationParams';
        setSaveFeedback('');
        setIsSavingMessage(true);
        fetch(url, {
            method: 'PUT',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': token,
            },
            body: JSON.stringify({ key: 'generalMessage', value: generalMessageInput }),
        })
            .then(response => {
                if (response.ok) {
                    setGeneralMessage(generalMessageInput);
                    setSaveFeedback('Boodschap opgeslagen.');
                } else {
                    setSaveFeedback('Opslaan mislukt.');
                }
            })
            .catch(() => {
                setSaveFeedback('Opslaan mislukt.');
            })
            .finally(() => {
                setIsSavingMessage(false);
            });
    }

    function toggleShopOpen() {
        toggleShopTo("True", "OPEN");
    }

    function toggleShopClosed() {
        toggleShopTo("False", "CLOSE");
    }

    return (
        <div className="page-wrap">
            {!token && (
                <section className="admin-card anim-rise" style={{ maxWidth: '520px', margin: '0 auto' }}>
                    <div className="header-row">
                        <h2 className="headline">Admin Login</h2>
                    </div>
                    <p className="subtle-text">Log in om producten, uploads en storefront instellingen te beheren.</p>
                    <div style={{ marginTop: '14px' }}>
                        {isRestoringSession && <div className="info-banner" role="status" aria-live="polite">Sessie herstellen...</div>}
                        {errorMessage && <div className="error-banner" role="alert">{errorMessage}</div>}
                        <form onSubmit={handleLogin} className="form-grid">
                            <div className="field-group">
                                <label htmlFor="username">Username</label>
                                <input
                                    id="username"
                                    type="text"
                                    autoComplete="username"
                                    required
                                    value={username}
                                    onChange={(e) => setUsername(e.target.value)}
                                />
                            </div>
                            <div className="field-group">
                                <label htmlFor="password">Password</label>
                                <input
                                    id="password"
                                    type="password"
                                    autoComplete="current-password"
                                    required
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                />
                            </div>
                            <button className="btn-main" type="submit" disabled={isLoggingIn || isRestoringSession}>
                                {isLoggingIn ? 'Bezig met inloggen...' : 'Log in'}
                            </button>
                        </form>
                    </div>
                </section>
            )}

            {token && (
                <section className="admin-card anim-fade-in">
                    <div className="header-row">
                        <div>
                            <h2 className="headline">Admin Dashboard</h2>
                            <p className="subtle-text">Snelbeheer van de shopstatus en algemene bezoekersboodschap.</p>
                        </div>
                        <button className="btn-danger" type="button" onClick={logout}>Logout</button>
                    </div>

                    {errorMessage && <div className="error-banner" role="alert">{errorMessage}</div>}
                    {saveFeedback && <div className="ok-banner" style={{ marginBottom: '14px' }} role="status" aria-live="polite">{saveFeedback}</div>}

                    <div className="tile-grid" style={{ marginBottom: '14px' }}>
                        <Link to="/products" className="tile-link anim-rise">
                            <strong>Producten bewerken</strong>
                            <p className="subtle-text" style={{ marginTop: '6px' }}>Wijzig productgegevens en voorraadstatus.</p>
                        </Link>
                        <Link to="/upload" className="tile-link anim-rise">
                            <strong>Menulijst uploaden</strong>
                            <p className="subtle-text" style={{ marginTop: '6px' }}>Upload een CSV met productdata.</p>
                        </Link>
                    </div>

                    <div className="two-column">
                        <div className="admin-card anim-rise" style={{ boxShadow: 'none', padding: '14px' }}>
                            <h3 className="panel-title">Shopstatus</h3>
                            <p className="helper-note">
                                Huidige status: <strong>{shopOpen ? 'OPEN' : 'CLOSED'}</strong>
                            </p>
                            <div className="row-actions" style={{ marginTop: '10px' }}>
                                {!shopOpen && (
                                    <button className="btn-main" type="button" onClick={toggleShopOpen} disabled={isTogglingShop}>
                                        {isTogglingShop ? 'Opslaan...' : 'Open shop'}
                                    </button>
                                )}
                                {shopOpen && (
                                    <button className="btn-warn" type="button" onClick={toggleShopClosed} disabled={isTogglingShop}>
                                        {isTogglingShop ? 'Opslaan...' : 'Close shop'}
                                    </button>
                                )}
                            </div>
                        </div>

                        <div className="admin-card anim-rise" style={{ boxShadow: 'none', padding: '14px' }}>
                            <h3 className="panel-title">Algemene boodschap</h3>
                            <div className="field-group">
                                <label htmlFor="generalMessage">Tekst voor bezoekers</label>
                                <textarea
                                    id="generalMessage"
                                    value={generalMessageInput}
                                    onChange={(e) => setGeneralMessageInput(e.target.value)}
                                    rows={4}
                                />
                            </div>
                            <div className="row-actions" style={{ marginTop: '10px' }}>
                                <button className="btn-main" type="button" onClick={saveGeneralMessage} disabled={isSavingMessage}>
                                    {isSavingMessage ? 'Opslaan...' : 'Boodschap opslaan'}
                                </button>
                            </div>
                            <p className="helper-note" style={{ marginTop: '10px' }}>
                                Live waarde: {generalMessage ? generalMessage : 'geen boodschap ingesteld'}
                            </p>
                        </div>
                    </div>
                </section>
            )}
        </div>
    );
};

function getCookie(name) {
    let cookieArray = document.cookie.split(';'); // Split document.cookie string into an array by ';'
    for (let i = 0; i < cookieArray.length; i++) {
        let cookiePair = cookieArray[i].trim().split('='); // Split each individual cookie into a name/value pair
        if (cookiePair[0] === name) {
            return cookiePair[1]; // Return the value if the name matches
        }
    }
    return null; // Return null if the cookie with the given name is not found
}

function logout() {
    document.cookie = "token=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;";
    // Optionally, redirect the user to the login page or refresh the page
    window.location.href = '/login'; // Redirect to login page
}


export default Login;